import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { selectCurrentEnterprise } from 'store/modules/enterprise/selectors';
import { selectMyUser } from 'store/modules/myUser/selectors';
import { selectSitesAsArray } from 'store/modules/sites/selectors';
import { selectNumberOfUsers } from 'store/modules/users/selectors';

type PendoVisitor = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  createdAt?: number;
  enableSmsNotifications?: boolean;
  enableEmailNotifications?: boolean;
  landlineNumberCountryCode?: string | null;
  landlineNumber?: string | null;
  mobileNumberCountryCode?: string | null;
  mobileNumber?: string | null;
  emailVerified?: boolean;
};

type PendoAccount = {
  id: string;
  name: string;
  country?: string;
  createdAt?: number;
  industry?: string;
  numberOfSites?: number;
  numberOfUsers?: number;
};

type PendoInitializeOptions = {
  visitor: PendoVisitor;
  account?: PendoAccount;
};

declare global {
  interface Window {
    pendo?: {
      initialize: (options: PendoInitializeOptions) => void;
    };
  }
}

/**
 * Initialises Pendo with the current visitor (user) and account (enterprise) when
 * both are available. The Pendo script is loaded in public/index.html.
 */
function PendoInitialize(): null {
  const myUser = useSelector(selectMyUser);
  const enterprise = useSelector(selectCurrentEnterprise);
  const numberOfUsers = useSelector(selectNumberOfUsers);
  const sites = useSelector(selectSitesAsArray);

  useEffect(() => {
    if (
      myUser &&
      enterprise &&
      typeof window.pendo?.initialize === 'function'
    ) {
      window.pendo.initialize({
        visitor: {
          id: `${myUser.id}`,
          email: myUser.email,
          firstName: myUser.firstName,
          lastName: myUser.lastName,
          createdAt: myUser.createdAt,
          enableSmsNotifications: myUser.enableSmsNotifications,
          enableEmailNotifications: myUser.enableEmailNotifications,
          landlineNumberCountryCode: myUser.landlineNumberCountryCode,
          landlineNumber: myUser.landlineNumber,
          mobileNumberCountryCode: myUser.mobileNumberCountryCode,
          mobileNumber: myUser.mobileNumberFormatted,
          emailVerified: myUser.isEmailVerified
        },
        account: {
          id: `${enterprise.id}`,
          name: enterprise.name,
          country: enterprise.billingAddress?.country,
          createdAt: enterprise.createdAt,
          numberOfSites: sites.length,
          numberOfUsers
        }
      });
    }
  }, [myUser, enterprise, numberOfUsers, sites.length]);

  return null;
}

export default PendoInitialize;
